<template>
  <RevDrawer
    :name="MODAL_NAME"
    :title="i18n(translations.mainCountryModalTitle)"
  >
    <template #body="{ close }">
      <RevLoadingScreen
        v-if="isSubmitting"
        class="mt-5 self-center"
        :text="i18n(translations.updatingMainCountry)"
        variant="light"
      />
      <template v-if="!isSubmitting">
        <RevForm
          :id="FORM_ID"
          :initial-values="initialFormValues"
          :validate
          @submit="handleSubmit"
        >
          <template #default="{ values, errors }">
            <div class="mb-16">
              <RevInputSelect
                id="mainTaxResidenceCountrySelect"
                v-model="values.mainCountry"
                :error="errors.mainCountry"
                :label="i18n(translations.mainCountryLabel)"
                :options="formattedTaxResidenceCountryOptions"
              />
            </div>
            <RevButton
              id="edit_main_tax_residence_country_button"
              class="mt-7"
              :form="FORM_ID"
              full-width="always"
              type="submit"
              variant="primary"
            >
              {{ i18n(translations.submitButton) }}
            </RevButton>
            <RevLink
              id="cancel_form"
              class="m-auto mt-12 w-full"
              @click="close"
            >
              {{ i18n(translations.cancelButton) }}
            </RevLink>
          </template>
        </RevForm>
      </template>
    </template>
  </RevDrawer>
</template>

<script lang="ts" setup>
import { getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { type Country, sellerProfileAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputSelect, type Value } from '@ds/components/InputSelect'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'

import type { Error } from '~/utils/apiError'

import { MODAL_NAMES } from '../../../../../../Profile.constants'

import translations from './MainCountryModal.translations'

const { openSuccessToast, openErrorToast } = useTheToast()

const MODAL_NAME = MODAL_NAMES.EDIT_TAX_MAIN_RESIDENCE_COUNTRY

const i18n = useI18n()
const FORM_ID = 'edit_main_tax_residence_country_form'
const emit = defineEmits(['main-country-updated'])

type MainTaxResidenceCountryFormValue = {
  mainCountry: Value | Country
}

const props = withDefaults(
  defineProps<{
    mainCountry?: Country | null
    additionalCountries?: Country[]
    taxResidenceCountryOptions?: Country[]
  }>(),
  {
    mainCountry: null,
    additionalCountries: () => [],
    taxResidenceCountryOptions: () => [],
  },
)

const isSubmitting = ref(false)

const initialFormValues = computed<MainTaxResidenceCountryFormValue>(() => ({
  mainCountry: props.mainCountry as Value,
}))

const validate = makeValidate<MainTaxResidenceCountryFormValue>({
  mainCountry: [required(i18n(translations.required))],
})

const formattedTaxResidenceCountryOptions = computed(() => {
  return props.taxResidenceCountryOptions.map((countryCode) => {
    return {
      label: i18n.country(countryCode),
      value: countryCode,
    }
  })
})

async function handleSubmit(values: MainTaxResidenceCountryFormValue) {
  isSubmitting.value = true
  try {
    await $httpFetch(sellerProfileAPI.updateTaxResidenceCountries, {
      body: {
        mainResidenceCountry: values.mainCountry,
        otherResidenceCountries: props.additionalCountries,
      },
    })

    openSuccessToast({
      title: i18n(translations.mainCountryUpdateSuccessTitle),
      content: i18n(translations.mainCountryUpdateSuccessContent),
    })

    emit('main-country-updated')

    closeModal(MODAL_NAME)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as Error),
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>
